import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import figureFormatter from "@/services/utils/figureFormatter";
import { formatDate, formatToDdMmYy, dateOfBirthConvertToAge } from '@/services/utils/global';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

const dentalQuotationPrinter = () => {

    const { onlyCommaFormat, commaFormat } = figureFormatter();

    const exportToPDF = (company, salesOrder, userName, note = null) => {
        var doc = {
            pageSize: 'A4',
            pageMargins: getPageMargin(),
            header: getHeader(company),
            footer: getFooter(userName),
            content: getContent(salesOrder, note),
            styles: {
                header: {
                    fontSize: 24,
                }
            },
            defaultStyle: {
                color: 'black',
                fontSize: 10,
                font: 'SulaimanLipi'
            },
            info: {
                title: 'Quotation'
            }
        }

        pushIntoTable(doc, salesOrder)

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    const getPageMargin = () => {
        return [40, 100, 40, 80];
    }

    const getHeader = (company) => {
        return {
            margin: [40, 25, 40, 10],
            columns: [
                company.logo64
                    ? {
                        alignment: 'left',
                        image: company.logo64,
                        maxHeight: 60
                    }
                    : null,

                {
                    alignment: 'right',
                    stack: [
                        company.name     ? company.name                       : '',
                        company.address  ? 'Address: '  + company.address     : '',
                        company.phone    ? 'Phone: '    + company.phone       : '',
                        company.email    ? 'E-mail: '   + company.email       : '',
                        company.vat_no   ? 'VAT Reg No: ' + company.vat_no   : '',
                    ]
                }
            ]
        }
    }

    const getPatientDetails = (contact, fieldName) => {
        if (! contact.profileItems) return '';
        const field = contact.profileItems.find(info => info.field === fieldName)
        return (field && field.value) ?? '';
    }

    const getPatientMobile = (contact) => {
        if (contact.contact_mobile_no) return contact.contact_mobile_no.value;
        return getPatientDetails(contact, 'phone');
    }

    const getPatientName = (salesOrder, note) => {
        if (note && note.patient) return note.patient.full_name.toUpperCase();
        if (salesOrder.contact) return salesOrder.contact.name.toUpperCase();
        return '';
    }

    const getPatientPhone = (salesOrder, note) => {
        if (note && note.patient) return note.patient.mobile_no || '';
        if (salesOrder.contact) return getPatientMobile(salesOrder.contact);
        return '';
    }

    const getPatientAddress = (salesOrder, note) => {
        if (note && note.patient && note.patient.full_address) return 'Patient Address: ' + note.patient.full_address;
        if (salesOrder.contact && salesOrder.contact.full_address) return 'Patient Address: ' + salesOrder.contact.full_address;
        return '';
    }

    const getConsultantName = (note) => {
        if (! note) return '';
        if (note.consultant) return note.consultant.name;
        return '';
    }

    const capitalize = (text) => {
        if (! text || typeof text !== 'string') return '';
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    const getPatientAgeGender = (note) => {
        if (! note || ! note.patient) return '';
        const age = note.patient.birthday ? dateOfBirthConvertToAge(note.patient.birthday).year + ' years' : '';
        const gender = note.patient.gender ? capitalize(note.patient.gender) : '';
        if (age && gender) return `Age: ${age} | Gender: ${gender}`;
        if (age) return `Age: ${age}`;
        if (gender) return `Gender: ${gender}`;
        return '';
    }

    const getDentalServiceNo = (note) => {
        if (! note || ! note.admission_no) return '';
        return note.admission_no;
    }

    const getQuotationDate = (salesOrder, note) => {
        if (note && note.admission_date) return formatDate(new Date(note.admission_date));
        if (salesOrder.date) return formatToDdMmYy(salesOrder.date);
        return formatDate(new Date());
    }

    const getContent = (salesOrder, note) => {
        return [
            {
                text: 'QUOTATION',
                style: 'header',
                alignment: 'center',
                margin: [0, 5, 20, 0],
                bold: true,
                color: 'black',
            },

            {
                margin: [0, 10, 0, 0],
                alignment: 'justify',
                columns: [
                    {
                        width: '50%',
                        stack: [
                            {
                                text: `Patient Name: ${getPatientName(salesOrder, note)}`,
                                alignment: 'left',
                                width: '50%'
                            },
                            {
                                text: `Patient Phone No: ${getPatientPhone(salesOrder, note)}`,
                                alignment: 'left',
                                width: '50%'
                            },
                            {
                                text: getPatientAgeGender(note),
                                alignment: 'left',
                                width: '50%'
                            },
                            {
                                text: getPatientAddress(salesOrder, note),
                                alignment: 'left',
                                width: '50%'
                            },
                            {
                                text: `${getConsultantName(note) ? 'Consultant: ' + getConsultantName(note) : ''}`,
                                alignment: 'left',
                                width: '50%'
                            }
                        ]
                    },

                    {
                        alignment: 'right',
                        stack: [
                            {
                                text: [
                                    { text: 'QUOTATION DATE: ', bold: true, margin: [0, 0, 10, 0] },
                                    { text: getQuotationDate(salesOrder, note) }
                                ]
                            },
                            {
                                text: [
                                    { text: 'ORDER NO: ', bold: true, margin: [0, 0, 10, 0] },
                                    { text: salesOrder.bill_number || '' }
                                ]
                            },
                            {
                                text: `${getDentalServiceNo(note) ? 'Dental Service No: ' + getDentalServiceNo(note) : ''}`
                            },
                        ]
                    },
                ]
            },

            {
                style: 'tableExample',
                margin: [0, 10, 0, 0],
                table: {
                    widths: ['auto', '*', 'auto', 'auto', 'auto'],
                    body: []
                },
                layout: {
                    fillColor: function (rowIndex, node, columnIndex) {
                        return (rowIndex === 0) ? '#f3f2f7' : null;
                    },
                    hLineWidth: function (i, node) { return 0.5; },
                    vLineWidth: function (i, node) { return 0.5; }
                }
            },

            {
                columns: [
                    { width: '65%', text: '' },
                    {
                        table: {
                            widths: ['*', '*'],
                            alignment: 'right',
                            body: totalBodyData(salesOrder)
                        },
                        layout: {
                            hLineWidth: function (i, node) { return 0.5; },
                            vLineWidth: function (i, node) { return 0.5; }
                        }
                    },
                ],
                margin: [0, 10]
            },

            {
                alignment: 'justify',
                text: [{ text: salesOrder.description ? 'NOTE: \n' : '', bold: true }, { text: salesOrder.description }]
            },
        ]
    }

    const totalBodyData = (salesOrder) => {
        const tableBody = [];

        tableBody.push([
            { text: 'Sub Total', alignment: 'right', bold: true },
            { text: commaFormat((salesOrder.subtotal_amount || 0).toFixed(2)), alignment: 'right' }
        ]);

        if (salesOrder.vat_amount) {
            tableBody.push([
                { text: 'VAT', alignment: 'right', bold: true },
                { text: onlyCommaFormat(salesOrder.vat_amount.toFixed(2)), alignment: 'right' }
            ]);
        }

        tableBody.push([
            { text: 'Total Amount', alignment: 'right', bold: true },
            { text: commaFormat((salesOrder.total_amount || 0).toFixed(2)), alignment: 'right' }
        ]);

        return tableBody;
    }

    const pushIntoTable = (doc, salesOrder) => {
        const items = salesOrder.general || [];

        doc.content[2].table.body.push(getTableHeader());

        items.forEach((item, index) => {
            doc.content[2].table.body.push(getRowData(item, index));
        });
    }

    const getTableHeader = () => {
        return [
            { text: 'SL' },
            { text: 'Particulars' },
            { text: 'Qty',        alignment: 'right' },
            { text: 'Unit Price', alignment: 'right' },
            { text: 'Amount',     alignment: 'right' }
        ];
    }

    const getRowData = (item, index) => {
        return [
            { text: index + 1 },
            { text: item.product ? item.product.name : '' },
            { text: onlyCommaFormat(item.quantity), alignment: 'right' },
            { text: onlyCommaFormat(item.rate), alignment: 'right' },
            { text: onlyCommaFormat(item.total_amount), alignment: 'right' }
        ];
    }

    const getFooter = (userName) => {
        return {
            margin: [40, 0, 40, 0],
            columns: [
                {
                    width: '80%',
                    alignment: 'left',
                    fontSize: 8,
                    text: 'N.B: This is a system generated quotation, not a final invoice.',
                    margin: [5, 33, 0, 0],
                },
                {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        { text: 'Printed by', bold: true },
                        { text: userName },
                        { text: formatDate(new Date()) }
                    ]
                }
            ]
        }
    }

    return {
        exportToPDF
    }
}

export default dentalQuotationPrinter;
